import { useState, useEffect, useMemo } from "react";
import { catalogService } from "@/services";
import type { AvatarStyleOption } from "@/lib/types";

interface StyleOverride {
  style_id: string;
  display_name: string | null;
  emoji: string | null;
  max_depth: number;
  is_active: boolean;
}

/**
 * Fetches custom style labels from style_taxonomy_config and
 * returns a function to apply overrides to any AvatarStyleOption[].
 */
export function useStyleLabels() {
  const [overrides, setOverrides] = useState<Record<string, StyleOverride>>({});

  useEffect(() => {
    const fetch = async () => {
      const { data } = await catalogService.listStyleTaxonomy();
      if (data) {
        const map: Record<string, StyleOverride> = {};
        for (const row of data) map[row.style_id] = row;
        setOverrides(map);
      }
    };
    fetch();
  }, []);

  /** Apply custom display_name and emoji overrides to a list of styles, filtering out inactive */
  const applyOverrides = useMemo(() => {
    return (styles: AvatarStyleOption[], filterActive = true): AvatarStyleOption[] =>
      styles
        .filter((s) => {
          if (!filterActive) return true;
          const o = overrides[s.id];
          // If no override exists, keep the style (backward compat)
          return o ? o.is_active !== false : true;
        })
        .map((s) => {
          const o = overrides[s.id];
          if (!o) return s;
          return {
            ...s,
            label: o.display_name || s.label,
            emoji: o.emoji || s.emoji,
          };
        });
  }, [overrides]);

  const getLabel = (styleId: string, fallback: string) =>
    overrides[styleId]?.display_name || fallback;

  const getEmoji = (styleId: string, fallback: string) =>
    overrides[styleId]?.emoji || fallback;

  return { applyOverrides, getLabel, getEmoji, overrides };
}
